import { useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import type { Product, Subscription } from '../api/types';
import { DataGrid } from './DataGrid';
import { ErrorBanner, SuccessBanner } from './ui';
import { Button, CheckboxInput, NumberInput, SelectInput, TextInput } from '../neon/controls';
import { Modal } from '../neon/Modal';
import { useDomId } from '../hooks/useDomId';
import { fmtDate, fmtMoney, toDateInput } from '../utils/format';

export interface SubscriptionsTabProps {
  subscriptions: Subscription[];
  products: Product[];
  loading: boolean;
  onReload: () => void;
  add: (body: Record<string, any>) => Promise<unknown>;
  update: (productId: number, body: Record<string, any>) => Promise<unknown>;
  remove: (productId: number) => Promise<unknown>;
  showEnrollment?: boolean;
}

interface Draft {
  productId: number | '';
  startDate: string;
  expiration: string;
  renewalPrice: string;
  enrollment: string;
  studentPrice: string;
  demoMode: boolean;
  editing: boolean;
}

const emptyDraft = (): Draft => ({
  productId: '',
  startDate: '',
  expiration: '',
  renewalPrice: '',
  enrollment: '',
  studentPrice: '',
  demoMode: false,
  editing: false,
});

export function SubscriptionsTab({
  subscriptions,
  products,
  loading,
  onReload,
  add,
  update,
  remove,
  showEnrollment = true,
}: SubscriptionsTabProps) {
  const formId = useDomId('sub');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const subscribedIds = useMemo(() => new Set(subscriptions.map((s) => s.productId)), [subscriptions]);
  const availableProducts = useMemo(
    () => products.filter((p) => !subscribedIds.has(p.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [products, subscribedIds],
  );

  const columns = useMemo<ColDef<Subscription>[]>(() => {
    const cols: ColDef<Subscription>[] = [
      { headerName: 'Product', field: 'productName', minWidth: 200 },
      { headerName: 'Start', field: 'startDate', valueFormatter: (p) => fmtDate(p.value), maxWidth: 140 },
      { headerName: 'Expiration', field: 'expiration', valueFormatter: (p) => fmtDate(p.value), maxWidth: 140 },
      { headerName: 'Renewal', field: 'renewalPrice', valueFormatter: (p) => fmtMoney(p.value), maxWidth: 120 },
    ];
    if (showEnrollment) {
      cols.push(
        { headerName: 'Enrollment', field: 'enrollment', maxWidth: 130 },
        { headerName: 'Student $', field: 'studentPrice', valueFormatter: (p) => fmtMoney(p.value), maxWidth: 120 },
      );
    }
    cols.push({
      headerName: 'Demo',
      field: 'demoMode',
      maxWidth: 100,
      valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
    });
    return cols;
  }, [showEnrollment]);

  const startAdd = () => {
    setError(null);
    setMessage(null);
    setDraft(emptyDraft());
  };

  const startEdit = (s: Subscription) => {
    setError(null);
    setMessage(null);
    setDraft({
      productId: s.productId,
      startDate: toDateInput(s.startDate),
      expiration: toDateInput(s.expiration),
      renewalPrice: s.renewalPrice != null ? String(s.renewalPrice) : '',
      enrollment: s.enrollment != null ? String(s.enrollment) : '',
      studentPrice: s.studentPrice != null ? String(s.studentPrice) : '',
      demoMode: s.demoMode,
      editing: true,
    });
  };

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const doSave = async () => {
    if (!draft || draft.productId === '') {
      setError('Please choose a product.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, any> = {
        startDate: draft.startDate || null,
        expiration: draft.expiration || null,
        renewalPrice: draft.renewalPrice === '' ? null : Number(draft.renewalPrice),
        demoMode: draft.demoMode ? 1 : 0,
      };
      if (showEnrollment) {
        body.enrollment = draft.enrollment === '' ? null : Number(draft.enrollment);
        body.studentPrice = draft.studentPrice === '' ? null : Number(draft.studentPrice);
      }
      if (draft.editing) {
        await update(Number(draft.productId), body);
        setMessage('Subscription updated.');
      } else {
        await add({ productId: Number(draft.productId), ...body });
        setMessage('Subscription added.');
      }
      setDraft(null);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (productId: number) => {
    if (!window.confirm('Remove this subscription?')) return;
    setSaving(true);
    setError(null);
    try {
      await remove(productId);
      setMessage('Subscription removed.');
      if (draft && Number(draft.productId) === productId) setDraft(null);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Legacy calculator: total = enrollment × student price. Defaults pre-fills a
  // one-year term at the default student price (1); Calculate copies total into
  // the renewal price.
  const total = draft && showEnrollment ? (Number(draft.enrollment) || 0) * (Number(draft.studentPrice) || 0) : 0;
  const today = () => new Date().toISOString().slice(0, 10);
  const plusYear = (d: string) => {
    const dt = d ? new Date(d) : new Date();
    dt.setFullYear(dt.getFullYear() + 1);
    return dt.toISOString().slice(0, 10);
  };
  const applyDefaults = () =>
    setDraft((d) => {
      if (!d) return d;
      const start = today();
      const studentPrice = '1';
      const renewalPrice = String((Number(d.enrollment) || 0) * Number(studentPrice));
      return { ...d, startDate: start, expiration: plusYear(start), studentPrice, renewalPrice };
    });
  const calcTotal = () =>
    setDraft((d) => (d ? { ...d, renewalPrice: String((Number(d.enrollment) || 0) * (Number(d.studentPrice) || 0)) } : d));

  return (
    <div className="tab-content">
      <div className="toolbar">
        <h3>Subscriptions ({subscriptions.length})</h3>
        <Button
          id="subs-add"
          text="Add subscription"
          icon="add"
          onClick={startAdd}
          disabled={availableProducts.length === 0}
        />
      </div>

      <SuccessBanner message={message} />
      <ErrorBanner error={error} />

      <DataGrid<Subscription>
        rows={subscriptions}
        columns={columns}
        loading={loading}
        getRowId={(r) => String(r.productId)}
        onRowClick={startEdit}
        height={360}
      />

      {draft && (
        <Modal
          title={draft.editing ? 'Edit subscription' : 'Add subscription'}
          size="medium"
          onClose={() => setDraft(null)}
          footer={
            <>
              <Button id={`${formId}-save`} text={saving ? 'Saving…' : 'Save subscription'} onClick={() => void doSave()} loading={saving} />
              <Button id={`${formId}-cancel`} text="Cancel" variant="tertiary" onClick={() => setDraft(null)} disabled={saving} />
              {draft.editing ? (
                <Button
                  id={`${formId}-remove`}
                  text="Remove"
                  variant="secondary"
                  icon="trash"
                  onClick={() => onDelete(Number(draft.productId))}
                  disabled={saving}
                />
              ) : null}
            </>
          }
        >
          <ErrorBanner error={error} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void doSave();
            }}
          >
            <div className="form-grid">
              {draft.editing ? (
                <TextInput
                  id={`${formId}-product`}
                  label="Product"
                  value={subscriptions.find((s) => s.productId === draft.productId)?.productName ?? ''}
                  onChange={() => {}}
                  readOnly
                />
              ) : (
                <SelectInput
                  id={`${formId}-product`}
                  label="Product"
                  value={draft.productId === '' ? '' : String(draft.productId)}
                  onChange={(v) => set({ productId: v === '' ? '' : Number(v) })}
                  options={availableProducts.map((p) => ({ value: p.id, label: p.name }))}
                  allowEmpty
                  emptyText="— select product —"
                  showFilter
                />
              )}
              <TextInput id={`${formId}-start`} label="Start date" value={draft.startDate} onChange={(v) => set({ startDate: v })} placeholder="YYYY-MM-DD" />
              <TextInput id={`${formId}-exp`} label="Expiration" value={draft.expiration} onChange={(v) => set({ expiration: v })} placeholder="YYYY-MM-DD" />
              {showEnrollment && (
                <>
                  <NumberInput id={`${formId}-enrollment`} label="Enrollment" value={draft.enrollment === '' ? undefined : Number(draft.enrollment)} onChange={(v) => set({ enrollment: v == null ? '' : String(v) })} integer />
                  <NumberInput id={`${formId}-studentprice`} label="Student price" value={draft.studentPrice === '' ? undefined : Number(draft.studentPrice)} onChange={(v) => set({ studentPrice: v == null ? '' : String(v) })} />
                </>
              )}
              <NumberInput id={`${formId}-renewal`} label="Renewal price" value={draft.renewalPrice === '' ? undefined : Number(draft.renewalPrice)} onChange={(v) => set({ renewalPrice: v == null ? '' : String(v) })} />
              <CheckboxInput id={`${formId}-demo`} label="Demo mode" checked={draft.demoMode} onChange={(v) => set({ demoMode: v })} />
            </div>
            {showEnrollment && (
              <div className="calc-bar">
                <span className="calc-total">
                  Total (enrollment × student price): <strong>{`$${total.toLocaleString()}`}</strong>
                </span>
                <Button id={`${formId}-calc`} text="Calculate → renewal" variant="secondary" onClick={calcTotal} />
                <Button id={`${formId}-defaults`} text="Defaults" variant="tertiary" onClick={applyDefaults} />
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
